import { Link } from "react-router-dom";
import { ArrowUpRight, Compass, Users, Layers3 } from "lucide-react";

const VISION_IMG =
  "https://images.unsplash.com/photo-1767978175944-a2e7045bbfea?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800";

const PRINCIPLES = [
  { icon: Compass, k: "Direction", v: "Build technology that has measurable impact on how people learn and live." },
  { icon: Users, k: "People", v: "Create platforms where individuals are empowered, not optimised away." },
  { icon: Layers3, k: "Scale", v: "Engineer for resilience — products that compound in value over decades." },
];

export default function Vision() {
  return (
    <div data-testid="page-vision" className="ihm-page-enter">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-[0.16]"
          style={{ backgroundImage: `url(${VISION_IMG})` }}
        />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-white" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-24 md:pt-32 pb-32">
          <span className="ihm-chip">Our Vision</span>
          <h1
            data-testid="vision-title"
            className="mt-8 font-heading font-bold text-5xl md:text-7xl lg:text-[88px] tracking-tighter leading-[1.02] max-w-6xl"
          >
            Technology that{" "}
            <span className="text-[#2997FF]">redefines</span> what people can
            learn, build, and become.
          </h1>
        </div>
      </section>

      {/* Statement */}
      <section className="border-t border-[#E5E5EA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#86868B]">
                Vision Statement
              </span>
            </div>
            <div className="lg:col-span-8">
              <p
                data-testid="vision-paragraph-1"
                className="text-[22px] md:text-[28px] leading-snug tracking-tight text-[#1D1D1F] font-heading"
              >
                Our vision is to build scalable and impactful technology
                solutions that redefine education, intelligence, and human
                capability.
              </p>
              <p
                data-testid="vision-paragraph-2"
                className="mt-8 text-[18px] leading-relaxed text-[#6E6E73] max-w-3xl"
              >
                We aim to create a future where technology empowers individuals,
                enhances learning, and unlocks new possibilities across
                industries.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="bg-[#F5F5F7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#86868B]">
                Operating principles
              </span>
              <h2 className="mt-4 font-heading font-bold text-4xl md:text-5xl tracking-tighter">
                The way we build.
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PRINCIPLES.map((p, i) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.k}
                  data-testid={`vision-principle-${i}`}
                  className="ihm-card p-8 md:p-10"
                >
                  <Icon size={22} className="text-[#2997FF]" />
                  <h3 className="mt-5 font-heading font-semibold text-2xl tracking-tight">
                    {p.k}
                  </h3>
                  <p className="mt-4 text-[15px] leading-relaxed text-[#6E6E73]">
                    {p.v}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8 border-t border-[#E5E5EA] pt-16">
            <h3 className="font-heading font-semibold text-3xl md:text-4xl tracking-tight max-w-2xl">
              Build with us. Or just say hello.
            </h3>
            <Link
              to="/contact"
              data-testid="vision-contact-cta"
              className="inline-flex items-center gap-3 rounded-full bg-[#1D1D1F] px-7 py-4 text-[15px] font-medium text-white transition-all duration-500 hover:bg-[#2997FF]"
            >
              Contact us
              <ArrowUpRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
